import { useCallback } from "react";

import { useSession, type RecruiterSession } from "@/lib/auth";

// FEAT-28 + FEAT-39 — the four team roles, as the browser sees them.
//
// `backend/permissions.py` is the one place a role is granted anything, the
// owner's matrix (Settings → Roles & permissions) narrows it per company, and
// `/api/auth/me.permissions` is the RESOLVED list for this person. Every
// hidden button, nav item and page notice on `/admin` reads that list through
// `useCan`, never `role` — so the UI can only ever disagree with a 403 if the
// server disagrees with itself (the same deal `usePlanAccess` has with the
// package matrix).
//
// What lives here that the server does not send: the WORDS — a label and one
// line per role. Display, not access: a wrong line here mislabels a role; it
// cannot open a door. The BOX labels (Emilia's words) live in
// `lib/rolePermissions.ts`.

export type TeamRole = "owner" | "member" | "external" | "hiring_manager";

/** The CAPABILITIES fixed by the role (`backend/permissions.py` CAP_*). */
export type RoleCapability =
  /** Billing writes, the workspace's name and closure, legal details, owners,
   *  the Notifications and Security tabs — and the matrix itself. */
  | "workspace.own"
  /** Every content and candidate write Emilia's list does not name. */
  | "workspace.write"
  | "results.review"
  | "results.score"
  | "account.self";

/** The BOXES the owner ticks per role (`backend/permissions.py` BOX_*). */
export type RoleBox =
  | "candidates.delete"
  | "candidates.export"
  | "jobs.own_only"
  | "tests.create"
  | "tests.delete"
  | "tests.archive"
  | "jobs.delete"
  | "jobs.archive"
  | "questions.delete"
  | "questions.archive"
  | "templates.delete"
  | "users.manage"
  | "users.hiring_managers"
  | "users.permissions"
  | "settings.export"
  | "settings.invitations"
  | "settings.branding"
  | "settings.messages"
  | "settings.candidate_form"
  | "settings.completion"
  | "settings.results"
  | "settings.integrations"
  | "settings.ai"
  | "tags.manage"
  | "billing.view";

/** Anything a door can ask for. */
export type Capability = RoleCapability | RoleBox;

/** The three users boxes — any one opens the Team page. */
export const USERS_BOXES: readonly Capability[] = [
  "users.manage",
  "users.hiring_managers",
  "users.permissions",
];

/** One box per Settings tab (+ the owner's own capability for the two tabs
 *  not in Emilia's list). Any one opens the Settings page. */
export const SETTINGS_BOXES: readonly Capability[] = [
  "settings.invitations",
  "settings.branding",
  "settings.messages",
  "settings.candidate_form",
  "settings.completion",
  "settings.results",
  "settings.export",
  "settings.integrations",
  "settings.ai",
];
export const SETTINGS_ANY: readonly Capability[] = [
  ...SETTINGS_BOXES,
  "workspace.own",
];

const REVIEWER: readonly Capability[] = [
  "results.review",
  "results.score",
  "account.self",
];

/** Display-side twin of the server's DEFAULT matrix — read ONLY when the
 *  session predates the `permissions` field (a tab left open across the
 *  deploy). The owner's company may have changed it; the server's list wins
 *  the moment it arrives. */
const FALLBACK: Record<TeamRole, readonly Capability[]> = {
  owner: [
    ...REVIEWER,
    "workspace.write",
    "workspace.own",
    "candidates.delete",
    "candidates.export",
    "tests.create",
    "tests.delete",
    "tests.archive",
    "jobs.delete",
    "jobs.archive",
    "questions.delete",
    "questions.archive",
    "templates.delete",
    "users.manage",
    "users.hiring_managers",
    "users.permissions",
    ...SETTINGS_BOXES,
    "tags.manage",
    "billing.view",
  ],
  member: [
    ...REVIEWER,
    "workspace.write",
    "candidates.delete",
    "candidates.export",
    "tests.create",
    "tests.delete",
    "tests.archive",
    "jobs.delete",
    "jobs.archive",
    "questions.delete",
    "questions.archive",
    "templates.delete",
    "tags.manage",
    "billing.view",
  ],
  external: [...REVIEWER, "workspace.write", "jobs.own_only", "tests.create"],
  hiring_manager: [...REVIEWER, "jobs.own_only", "candidates.export"],
};

// ⚠ Placeholder copy — the wording is Emilia's (owed.md). "External recruiter
// / Sourcer" is her own name for the role (Sheet #144).
export const ROLE_LABEL: Record<TeamRole, string> = {
  owner: "Owner",
  member: "Recruiter",
  external: "External recruiter / Sourcer",
  hiring_manager: "Hiring manager",
};

/** "…join the team as ___" — the accept-invite page's sentence. */
export const ROLE_JOIN_AS: Record<TeamRole, string> = {
  owner: "an owner",
  member: "a recruiter",
  external: "an external recruiter",
  hiring_manager: "a hiring manager",
};

export const ROLE_DESCRIPTION: Record<TeamRole, string> = {
  owner:
    "Everything, including billing, closing the workspace, managing owners and setting what each role can do.",
  member:
    "Creates tests and recruitments, invites candidates and reviews results. What else they can do is set in Settings → Roles & permissions.",
  external:
    "Works only in their own recruitments: creates tests there and invites candidates. No deleting, archiving, exporting or settings unless an owner allows it.",
  hiring_manager:
    "Sees the results of the recruitments shared with them, reviews candidates and scores open answers. Can't create tests or invite.",
};

/** Most powerful first — the order every picker lists them in. */
export const ROLE_ORDER: readonly TeamRole[] = [
  "owner",
  "member",
  "external",
  "hiring_manager",
];

function isTeamRole(role: string): role is TeamRole {
  return role in ROLE_LABEL;
}

/** A role string from the server → its label. An unknown one (a newer deploy,
 *  or the retired `admin`) is shown as written rather than mislabelled. */
export function roleLabel(role: string): string {
  return isTeamRole(role) ? ROLE_LABEL[role] : role;
}

export function roleDescription(role: string): string {
  return isTeamRole(role) ? ROLE_DESCRIPTION[role] : "";
}

type SessionRole = Pick<RecruiterSession, "role" | "permissions">;

function grantedOf(session: SessionRole): readonly string[] {
  return (
    session.permissions ??
    (isTeamRole(session.role) ? FALLBACK[session.role] : REVIEWER)
  );
}

/**
 * May this session do that? Reads the server's list; falls back to the
 * default matrix for a session cached before the list existed. An unknown
 * role holds only what a hiring manager holds — the server's own fail-closed
 * rule.
 */
export function can(
  session: SessionRole | null | undefined,
  capability: Capability,
): boolean {
  if (!session) return false;
  return grantedOf(session).includes(capability);
}

/** ANY of several — the doors the server opens to more than one box (the
 *  Team page, the Settings page, the branding document). */
export function canAny(
  session: SessionRole | null | undefined,
  capabilities: readonly Capability[],
): boolean {
  if (!session) return false;
  const granted = grantedOf(session);
  return capabilities.some((c) => granted.includes(c));
}

/**
 * What a ROLE holds in THIS company — read from `GET /api/admin/team`'s
 * `role_permissions` (the resolved matrix), for reasoning about someone ELSE
 * (a teammate's row). Without it, the default. What this session may do
 * still reads `useCan`.
 */
export function roleHolds(
  role: string,
  capability: Capability,
  rolePermissions?: Record<string, readonly string[]>,
): boolean {
  const granted =
    rolePermissions?.[role] ??
    (isTeamRole(role) ? FALLBACK[role] : REVIEWER);
  return granted.includes(capability);
}

/** 10-07 S4 + FEAT-39: the role change that revokes a person's API keys and
 *  webhooks — from a role holding Emilia's 'Integrations' box to one that
 *  does not. Mirrors team.py / `permissions.loses_integrations`. */
export function losesIntegrations(
  from: string,
  to: string,
  rolePermissions?: Record<string, readonly string[]>,
): boolean {
  return (
    roleHolds(from, "settings.integrations", rolePermissions) &&
    !roleHolds(to, "settings.integrations", rolePermissions)
  );
}

/**
 * 10-07 S4: what a removal, or a role change that loses 'Integrations',
 * revokes — read from the team list's counts (the server revokes the same
 * set). Null when the person owns nothing, so no dialog says "0 API keys".
 * ⚠ Placeholder copy — the wording is Emilia's (owed.md).
 */
export function integrationsRevokedLine(member: {
  api_key_count?: number;
  webhook_count?: number;
}): string | null {
  const keys = member.api_key_count ?? 0;
  const hooks = member.webhook_count ?? 0;
  if (keys + hooks === 0) return null;
  const parts: string[] = [];
  if (keys) parts.push(`${keys} API key${keys === 1 ? "" : "s"}`);
  if (hooks) parts.push(`${hooks} webhook${hooks === 1 ? "" : "s"}`);
  return `${parts.join(" and ")} will be revoked — integrations using ${
    keys + hooks === 1 ? "it" : "them"
  } stop working.`;
}

/**
 * `const canDo = useCan(); canDo("workspace.write")`.
 *
 * ⚠ While the session is still loading this answers FALSE for everything, so a
 * write control never flashes in front of someone who may not use it. Pages
 * already render nothing until `RequireAuth` has a session.
 */
export function useCan(): (capability: Capability) => boolean {
  const { data: session } = useSession();
  return useCallback(
    (capability: Capability) => can(session, capability),
    [session],
  );
}

/** `useCanAny()([…])` — the any-of twin of `useCan`. */
export function useCanAny(): (capabilities: readonly Capability[]) => boolean {
  const { data: session } = useSession();
  return useCallback(
    (capabilities: readonly Capability[]) => canAny(session, capabilities),
    [session],
  );
}

/**
 * R19 (Leo, 2026-10-25): may this session write the company LIBRARY from the
 * Library and Templates pages — a library-only question, an import, "Add to
 * my library", delete / archive, every template write? Only with
 * `workspace.write` AND a view of every recruitment: a person limited to
 * selected recruitments (an external recruiter by default) authors inside
 * their own tests — adding a question there, editing one only their tests
 * use — and the server answers anything else `LIBRARY_NEEDS_ALL_RECRUITMENTS`.
 * Not a box (the scope decides, not the role), so it reads the same
 * `job_access_restricted` the server builds that scope from.
 */
export function canWriteLibrary(
  session:
    | (SessionRole & Pick<RecruiterSession, "job_access_restricted">)
    | null
    | undefined,
): boolean {
  return can(session, "workspace.write") && !session?.job_access_restricted;
}

export function useCanWriteLibrary(): boolean {
  const { data: session } = useSession();
  return canWriteLibrary(session);
}

// What an EMPTY list says to someone who cannot fill it. Every list page's
// empty state is a create-CTA ("New test", "Create job") — the right answer for
// a recruiter in a new workspace, and the wrong one for a hiring manager, who
// is looking at an empty page because nothing has been shared with them yet
// (a new hiring manager starts limited to ZERO recruitments).
// ⚠ Placeholder copy — the wording is Emilia's (owed.md).
export const NOTHING_SHARED_TITLE = "No recruitments shared with you yet";
export const NOTHING_SHARED_HINT =
  "You'll see candidates and results here once a workspace owner shares a recruitment with you.";
export const NOTHING_HERE_YET = "Nothing here yet.";

/**
 * The line an empty list shows: `forAuthors` for a role that can create what
 * is missing, otherwise why the page is empty — "nothing shared yet" when the
 * person is limited to selected recruitments, a plain "nothing here yet" when
 * they see the whole (empty) workspace.
 */
export function useEmptyWorkspaceCopy(forAuthors: string): string {
  const { data: session } = useSession();
  if (can(session, "workspace.write")) return forAuthors;
  return session?.job_access_restricted
    ? `${NOTHING_SHARED_TITLE}. ${NOTHING_SHARED_HINT}`
    : NOTHING_HERE_YET;
}

/** The server's 403 `{code: "ROLE_NOT_PERMITTED"}` (or the owner gate's
 *  plain string): the session's role, or its boxes, moved under an open tab. */
export function isRoleDenied(status: number, detail: unknown): boolean {
  if (status !== 403) return false;
  if (detail === "OWNER_ROLE_REQUIRED") return true;
  return (
    typeof detail === "object" &&
    detail !== null &&
    (detail as { code?: unknown }).code === "ROLE_NOT_PERMITTED"
  );
}
